import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertCircle, Phone, Mail, CheckCircle2 } from 'lucide-react';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-white text-sm mb-1 block">{label}{required && ' *'}</label>
    {children}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-[#141414] border border-[#262626] rounded-md p-6 ${className}`}>{children}</div>
);

const PoApprovePublic = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [form, setForm] = useState({ po_number: '', po_notes: '', approver_name: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/po-approve/${token}`)
      .then((r) => setState({ status: 'ready', data: r.data }))
      .catch((e) => {
        const d = e.response?.data?.detail || {};
        setState({ status: 'error', code: d.code || 'ERROR', message: d.message || 'Link is invalid.' });
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.po_number.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/po-approve/${token}`, form);
      setState({ status: 'done', data, prior: state.data });
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setState({ status: 'error', code: (typeof d === 'object' ? d.code : 'ERROR'), message: (typeof d === 'object' ? d.message : (d || e2.message)) });
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      <header className="border-b border-[#262626] py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Logo size="md" showTagline={false} />
          <span className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase border-l border-[#262626] pl-3">PO Quick-Approve</span>
        </div>
      </header>
      <main className="flex-1 px-4 py-10">
        <div className="max-w-2xl mx-auto">
          {state.status === 'loading' && <p className="text-[#A3A3A3]">Verifying link…</p>}

          {state.status === 'error' && (
            <Card>
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="w-6 h-6 text-red-400" />
                <h1 className="text-2xl text-white font-bold font-['Outfit']">Link unavailable</h1>
              </div>
              <p className="text-[#A3A3A3]">{state.message}</p>
              <p className="text-[#525252] text-xs mt-4">Need help? Call NexFlow at <a href="tel:6142854502" className="text-[#D4AF37]">614-285-4502</a> or email <a href="mailto:billing@nexflowplumbing.com" className="text-[#D4AF37]">billing@nexflowplumbing.com</a>.</p>
            </Card>
          )}

          {state.status === 'done' && (
            <Card>
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                <h1 className="text-2xl text-white font-bold font-['Outfit']">PO Received — Thank You</h1>
              </div>
              <p className="text-[#A3A3A3]">PO Number <span className="text-emerald-300 font-mono">{state.data.po_number}</span> has been attached to Work Order <span className="text-[#D4AF37] font-mono">{state.prior?.work_order_number}</span>. NexFlow will proceed with scheduling and invoicing under this PO.</p>
              <p className="text-[#525252] text-xs mt-6">You may close this page. A confirmation copy has been emailed to {state.prior?.billing_contact_name || 'billing'}.</p>
            </Card>
          )}

          {state.status === 'ready' && (
            <>
              <Card className="mb-4">
                <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Action Needed
                </p>
                <h1 className="text-2xl text-white font-bold font-['Outfit']">Enter a Purchase Order Number</h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
                  <div><p className="text-[#525252] text-[10px] uppercase tracking-wider">Work Order</p><p className="text-[#D4AF37] font-mono">{state.data.work_order_number}</p></div>
                  <div><p className="text-[#525252] text-[10px] uppercase tracking-wider">Property</p><p className="text-white">{state.data.property_name}{state.data.unit_number ? ` · Unit ${state.data.unit_number}` : ''}</p></div>
                  <div><p className="text-[#525252] text-[10px] uppercase tracking-wider">Company</p><p className="text-white">{state.data.company_name}</p></div>
                  <div><p className="text-[#525252] text-[10px] uppercase tracking-wider">Priority</p><p className="text-white">{state.data.priority}</p></div>
                  <div className="sm:col-span-2"><p className="text-[#525252] text-[10px] uppercase tracking-wider">Issue</p><p className="text-white">{state.data.issue_description}</p></div>
                </div>
              </Card>

              <Card>
                <form onSubmit={submit} className="space-y-4">
                  <Field label="PO Number" required>
                    <input
                      data-testid="public-po-number"
                      value={form.po_number}
                      onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
                      placeholder="e.g. PO-12345"
                      required
                      autoFocus
                    />
                  </Field>
                  <Field label="Your Name">
                    <input
                      data-testid="public-po-approver"
                      value={form.approver_name}
                      onChange={(e) => setForm({ ...form, approver_name: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
                      placeholder={state.data.billing_contact_name || 'Approver name'}
                    />
                  </Field>
                  <Field label="Notes (optional)">
                    <textarea
                      data-testid="public-po-notes"
                      rows={3}
                      value={form.po_notes}
                      onChange={(e) => setForm({ ...form, po_notes: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
                      placeholder="Notes for NexFlow billing"
                    />
                  </Field>
                  <button
                    data-testid="public-po-submit"
                    disabled={submitting || !form.po_number.trim()}
                    className={`w-full h-12 rounded font-bold transition-colors ${submitting || !form.po_number.trim() ? 'bg-[#262626] text-[#525252] cursor-not-allowed' : 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]'}`}
                  >
                    {submitting ? 'Submitting…' : 'Approve & Submit PO'}
                  </button>
                </form>
                <p className="text-[#525252] text-[10px] mt-4 text-center">Single-use link — expires {state.data.expires_at ? new Date(state.data.expires_at).toLocaleDateString() : 'in 7 days'}.</p>
              </Card>
            </>
          )}
        </div>
      </main>
      <footer className="border-t border-[#262626] py-4 px-6 text-center">
        <p className="text-[#525252] text-xs">© NexFlow Plumbing — <a href="tel:6142854502" className="text-[#A3A3A3] hover:text-[#D4AF37]"><Phone className="w-3 h-3 inline mr-1" />614-285-4502</a> · <a href="mailto:billing@nexflowplumbing.com" className="text-[#A3A3A3] hover:text-[#D4AF37]"><Mail className="w-3 h-3 inline mr-1" />billing@nexflowplumbing.com</a></p>
      </footer>
    </div>
  );
};

export default PoApprovePublic;
